import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronDown, Check, AlertCircle, Loader2, Table2, FileText, Pin } from "lucide-react";
import api, { baseURL } from "~/services/api";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const STORAGE_KEY = "student_pinned_filters";
const STORAGE_PINNED_KEY = "student_filters_pinned";

const EMPTY_FILTERS = {
  teacher: "",
  category: "",
  status: "",
  agent: "",
  processType: "",
  substatus: "",
  group: "",
  startDateFrom: "",
  startDateTo: "",
  registeredFrom: "",
  registeredTo: "",
};

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const DEFAULT_OPTIONS = {
  teachers: ["Teacher A", "Teacher B", "Teacher C"],
  categories: [
    { value: "Student", label: "Student" },
    { value: "Ex-student", label: "Ex-student" },
  ],
  status: ["In Class", "Waiting", "Inactive"],
  agents: ["Agent A", "Agent B"],
  processTypes: ["Type A", "Type B"],
  substatus: ["Substatus A", "Substatus B"],
};

const Toast = ({ message, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-5 duration-300">
      <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium text-sm">{message}</span>
        <button onClick={onClose} className="ml-2 hover:bg-red-700 p-1 rounded-full">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const DateInputWithClear = ({ value, onChange, className }) => {
  return (
    <div className="relative w-full">
      <input
        type="date"
        className={`${className} pr-10`}
        value={value}
        onChange={onChange}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange({ target: { value: "" } })}
          className="absolute top-1/2 right-9 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors bg-white hover:bg-gray-100"
          title="Clear date"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label ?? "")
      .toLowerCase()
      .includes(String(query ?? "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
        {label}
      </label>
      
      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  data = null,
  loading = false,
  applying = false,
  canExportExcel = true,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...EMPTY_FILTERS, ...JSON.parse(raw) } : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(() => localStorage.getItem(STORAGE_PINNED_KEY) === "true");

  const [toastState, setToastState] = useState({ show: false, message: "" });
  const [exportingExcel, setExportingExcel] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  const options = useMemo(() => {
    const sourceData = dynamicOptions || data;

    return {
      teachers:
        sourceData?.teachers?.map((t) => ({
          value: t.id,
          label: t.name,
        })) || DEFAULT_OPTIONS.teachers,
      categories:
        sourceData?.categories?.map((c) => ({
          value: c.id,
          label: c.name,
        })) || DEFAULT_OPTIONS.categories,
      status: DEFAULT_OPTIONS.status,
      agents:
        sourceData?.agents?.map((a) => ({
          value: a.id,
          label: a.name,
        })) || DEFAULT_OPTIONS.agents,
      processTypes:
        sourceData?.processtypes?.map((p) => ({
          value: p.id,
          label: p.name,
        })) || DEFAULT_OPTIONS.processTypes,
      substatus:
        sourceData?.processsubstatuses?.map((p) => ({
          value: p.id,
          label: p.name,
        })) || DEFAULT_OPTIONS.substatus,
      groups:
        sourceData?.groups?.map((g) => ({
          value: g.id,
          label: g.name,
        })) || [],
    };
  }, [data, dynamicOptions]);

  const inputBaseClasses =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500";
  
  const buttonBaseClasses =
    "px-4 py-2 text-sm font-medium border rounded-lg transition-colors shadow-xs flex-1 justify-center items-center flex";
  const secondaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const primaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const clearButtonClasses = "w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors mb-1";
  const excelButtonClasses = "flex-1 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center";
  const pdfButtonClasses = "flex-1 py-2 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed flex items-center justify-center opacity-50";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  };

  const fetchFilters = async (params = {}) => {
    setLoadingFilters(true);
    try {
      const { data: filtersData } = await api.get("/studentfilters", { params });
      setDynamicOptions(filtersData);
    } catch (error) {
      console.error("Error fetching dynamic filters:", error);
      setDynamicOptions(null);
    } finally {
      setLoadingFilters(false);
    }
  };

  useEffect(() => {
    if (!opened) return;

    const params = {};

    if (formData.teacher) {
      params.teacher_id = formData.teacher;
    }

    if (formData.group) {
      params.group_id = formData.group;
    }

    if (formData.teacher || formData.group) {
      fetchFilters(params);
    } else {
      setDynamicOptions(null);
    }
  }, [formData.teacher, formData.group, opened]);

  const buildQueryString = () => {
    const params = new URLSearchParams();

    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", "name");
    params.set("orderASC", "ASC");
    params.set("search", "");

    if (formData.teacher) params.set("teacher_id", formData.teacher);
    if (formData.category) params.set("category", formData.category);
    if (formData.status) params.set("status", formData.status);
    if (formData.agent) params.set("agent_id", formData.agent);
    if (formData.processType) params.set("processtype_id", formData.processType);
    if (formData.substatus) params.set("processsubstatus_id", formData.substatus);
    if (formData.group) params.set("group_id", formData.group);

    if (formData.startDateFrom) params.set("start_date_from", formData.startDateFrom);
    if (formData.startDateTo) params.set("start_date_to", formData.startDateTo);
    if (formData.registeredFrom) params.set("registered_from", formData.registeredFrom);
    if (formData.registeredTo) params.set("registered_to", formData.registeredTo);

    return `/students?${params.toString()}`;
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleApplyFilters = async () => {
    const searchableFields = [
      { key: "teacher", label: "Teacher", options: options.teachers },
      { key: "category", label: "Category", options: options.categories },
      { key: "status", label: "Status", options: options.status },
      { key: "agent", label: "Associate", options: options.agents },
      { key: "processType", label: "Process Type", options: options.processTypes },
      { key: "substatus", label: "Process Substatus", options: options.substatus },
      { key: "group", label: "Group", options: options.groups || [] },
    ];

    const invalidField = searchableFields.find((field) => {
      const value = formData[field.key];
      if (!value) return false;
      if (Array.isArray(field.options)) {
        return !field.options.some(
          (opt) =>
            (typeof opt === "string" && opt === value) ||
            (typeof opt === "object" && opt.value === value)
        );
      }
      return false;
    });

    if (invalidField) {
      setToastState({
        show: true,
        message: `Option Invalid, choose a option for ${invalidField.label}`
      });
      return;
    }

    if (pinned) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }

    const query = buildQueryString();
    console.log("Request preview:", query);
    await onApplyFilters(query);
    handleClose();
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = {};

      if (formData.teacher) params.teacher_id = formData.teacher;
      if (formData.category) params.category = formData.category;
      if (formData.status) params.status = formData.status;
      if (formData.agent) params.agent_id = formData.agent;
      if (formData.processType) params.processtype_id = formData.processType;
      if (formData.substatus) params.processsubstatus_id = formData.substatus;
      if (formData.group) params.group_id = formData.group;

      if (formData.startDateFrom) params.start_date_from = formData.startDateFrom;
      if (formData.startDateTo) params.start_date_to = formData.startDateTo;
      if (formData.registeredFrom) params.registered_from = formData.registeredFrom;
      if (formData.registeredTo) params.registered_to = formData.registeredTo;

      const { data } = await api.get("/students/excel", { params });
      const name = data?.name || `students_export.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel generated successfully!", { type: "success", autoClose: 2500 });
      handleClose();
    } catch (err) {
      console.error("Error exporting excel:", err);
      toast(err?.response?.data?.error || "Failed to generate Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <Toast
        show={toastState.show}
        message={toastState.message}
        onClose={() => setToastState({ ...toastState, show: false })}
      />

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
          isClosing ? 'modal-slide-out' : 'modal-slide-in'
        }`}>
          
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">Filters</h2>
              <p className="text-xs text-gray-500 font-medium">
                {loadingFilters ? (
                  <span className="flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" />
                    Updating options...
                  </span>
                ) : (
                  "Refine your search results"
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${pinned ? "bg-orange-100 text-orange-600 hover:bg-orange-200" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            {loading ? (
              <div className="flex items-center justify-center h-full gap-2 text-gray-500">
                <Loader2 className="animate-spin" size={18} />
                Loading filters...
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-5">
                  <SearchableSelect
                    label="Teacher"
                    options={options.teachers}
                    value={formData.teacher}
                    onChange={(val) => handleChange("teacher", val)}
                  />

                  <SearchableSelect
                    label="Category"
                    options={options.categories}
                    value={formData.category}
                    onChange={(val) => handleChange("category", val)}
                  />

                  <SearchableSelect
                    label="Status"
                    options={options.status}
                    value={formData.status}
                    onChange={(val) => handleChange("status", val)}
                  />

                  <SearchableSelect
                    label="Associate"
                    options={options.agents}
                    value={formData.agent}
                    onChange={(val) => handleChange("agent", val)}
                  />

                  <SearchableSelect
                    label="Process Type"
                    options={options.processTypes}
                    value={formData.processType}
                    onChange={(val) => handleChange("processType", val)}
                  />

                  <SearchableSelect
                    label="Process Substatus"
                    options={options.substatus}
                    value={formData.substatus}
                    onChange={(val) => handleChange("substatus", val)}
                  />
                  <SearchableSelect
                    label="Group"
                    options={options.groups}
                    value={formData.group}
                    onChange={(val) => handleChange("group", val)}
                  />
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-5 tracking-wider flex items-center gap-2">
                    Registered Date Range
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Registered From
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.registeredFrom}
                        onChange={(e) =>
                          handleChange("registeredFrom", e.target.value)
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Registered To
                      </label>
                      <DateInputWithClear
                        className={inputBaseClasses}
                        value={formData.registeredTo}
                        onChange={(e) =>
                          handleChange("registeredTo", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">

            {canExportExcel && (
              <button
                type="button"
                onClick={handleExportExcel}
                className={excelButtonClasses}
                disabled={exportingExcel || applying}
              >
                {exportingExcel ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Table2 size={16} className="mr-2" />
                    Export Excel
                  </>
                )}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setFormData(EMPTY_FILTERS);
                localStorage.removeItem(STORAGE_KEY);
              }}
              className={clearButtonClasses}
              disabled={applying || exportingExcel}
            >
              Clean All Filters
            </button>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className={secondaryButtonClasses}
                disabled={exportingExcel || applying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className={primaryButtonClasses}
                disabled={applying || exportingExcel}
              >
                {applying ? "Applying..." : "Apply Filters"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
